import axios from 'axios';

// Sign-in, password recovery and invitation calls answer 401 for a wrong password or a bad link;
// that is not a dead session.
const PUBLIC_AUTH_PATH = /\/users\/(login|forgot-password|reset-password|validate-reset-token|accept-invitation|invitation\/[^/]+)$/;

const readAuthorization = (headers) => {
  if (!headers) {
    return '';
  }
  if (typeof headers.get === 'function') {
    return String(headers.get('Authorization') || headers.get('authorization') || '');
  }
  return String(headers.Authorization || headers.authorization || '');
};

const requestPath = (url) => {
  try {
    return new URL(String(url || ''), window.location.origin).pathname;
  } catch {
    return String(url || '').split('?')[0];
  }
};

/**
 * True when the API refused a signed-in request's token (deactivated user, revoked session) rather
 * than the token simply having expired, which DashboardRuntimeGuard already handles.
 */
export const isRejectedSession = (error) => {
  if (error?.response?.status !== 401) {
    return false;
  }
  const config = error?.config || {};
  if (!/^Bearer\s+\S+/i.test(readAuthorization(config.headers))) {
    return false;
  }
  return !PUBLIC_AUTH_PATH.test(requestPath(config.url));
};

/**
 * Logs the user out once, with a notice on the login screen, the first time the API rejects the
 * session. Without it the dashboard stayed open with empty data and failing requests.
 */
export const installSessionInterceptor = ({ store, logout }) => {
  let handled = false;
  return axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!handled && isRejectedSession(error) && store.getState()?.userLogin?.userInfo) {
        handled = true;
        try {
          sessionStorage.setItem('dashboardSessionNotice', JSON.stringify({ reason: 'rejected', occurredAt: Date.now() }));
        } catch {
          // The notice is a courtesy; logging out matters more.
        }
        store.dispatch(logout());
      }
      return Promise.reject(error);
    },
  );
};
